//
// src/pages/Reports/NoShowLossReportPage.js
//
// No-Show Impact & Recovery Report UI
// ============================================================================
// PURPOSE
// -------
// This page presents management analytics for not-performed visits.
//
// IMPORTANT INTERPRETATION
// ------------------------
// This page should NOT be read as a simplistic final-loss statement.
//
// Instead, it separates:
// - gross at-risk value
// - estimated recovered value
// - net unrecovered value
//
// That makes the report more realistic for businesses where a portion of
// missed visits is later recovered through rebooking.
//
// UI GOALS
// --------
// 1) Keep the page visually aligned with the rest of the reports module.
// 2) Present the managerial story in the correct order:
//    gross risk -> recovery -> net impact.
// 3) Add charts that explain concentration and recovery quality quickly.
// 4) Preserve local search and lightweight interactivity.
// 5) Keep detail rows available for audit / review.
//
// I18N NOTE
// ---------
// This report intentionally keeps backend payload field names and technical
// recovery codes unchanged. Only user-facing labels, descriptions and chart
// legends are translated in the React layer. Backend methodology note_keys are
// translated by the frontend, while the legacy backend notes array remains a
// safe fallback for older payloads.
//

import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import AuthContext from "../../context/AuthContext";
import { fetchUsers } from "../../services/userService";
import { fetchNoShowLossReport } from "../../services/reportService";

import EvaluationPeriodPicker from "../../components/filters/EvaluationPeriodPicker";
import useEvaluationPeriod from "../../hooks/useEvaluationPeriod";
import { EVALUATION_PERIOD_TYPES } from "../../utils/evaluationPeriodUtils";

import {
  ResponsiveContainer,
  ComposedChart,
  BarChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Cell,
  ReferenceLine,
} from "recharts";

import "./NoShowLossReportPage.css";

/* -------------------------------------------------------------------------- */
/* Helpers                                                                    */
/* -------------------------------------------------------------------------- */

function toFiniteNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function formatMoney(value) {
  return toFiniteNumber(value, 0).toFixed(2);
}

function formatMinutes(value) {
  return toFiniteNumber(value, 0).toFixed(1);
}

function formatCount(value) {
  return String(Math.round(toFiniteNumber(value, 0)));
}

function formatPercentFromRatio(value) {
  return `${(toFiniteNumber(value, 0) * 100).toFixed(1)}%`;
}

const CHART_COLORS = Object.freeze({
  gross: "#2563eb",
  recovered: "#16a34a",
  net: "#dc2626",
  rate: "#7c3aed",
  grid: "#e5e7eb",
  axis: "#6b7280",
});

function resolveMethodologyNotes(methodology, t) {
  /**
   * Backend methodology text migration helper.
   *
   * The backend now sends stable methodology.note_keys so the frontend can own
   * the visible language through reports.json. Legacy methodology.notes remain
   * supported as a backward-compatible fallback while the report endpoints are
   * migrated incrementally.
   */
  const reportKind = methodology?.report_kind;

  if (
    reportKind &&
    Array.isArray(methodology?.note_keys) &&
    methodology.note_keys.length > 0
  ) {
    return methodology.note_keys.map((noteKey) =>
      t(`reports:methodology.${reportKind}.notes.${noteKey}`, {
        defaultValue: noteKey,
      })
    );
  }

  return Array.isArray(methodology?.notes) ? methodology.notes : [];
}

/* -------------------------------------------------------------------------- */
/* Tooltips                                                                   */
/* -------------------------------------------------------------------------- */

function ImpactBreakdownTooltip({ active, payload, label, t }) {
  if (!active || !Array.isArray(payload) || payload.length === 0) {
    return null;
  }

  const row = payload?.[0]?.payload;
  if (!row) return null;

  return (
    <div className="chart-tooltip chart-tooltip--rich">
      <div className="chart-tooltip__title">{label}</div>

      <div className="chart-tooltip__grid">
        <div>
          <span>{t("reports:noShow.grossAtRiskRevenue")}</span>
          <strong>{formatMoney(row.gross_at_risk_revenue)} PLN</strong>
        </div>
        <div>
          <span>{t("reports:noShow.recoveredRevenue")}</span>
          <strong>{formatMoney(row.recovered_revenue)} PLN</strong>
        </div>
        <div>
          <span>{t("reports:noShow.netUnrecoveredRevenue")}</span>
          <strong>{formatMoney(row.net_unrecovered_revenue)} PLN</strong>
        </div>
        <div>
          <span>{t("reports:noShow.recoveryRate")}</span>
          <strong>{formatPercentFromRatio(row.recovery_rate_revenue)}</strong>
        </div>
        <div>
          <span>{t("reports:noShow.noShowVisits")}</span>
          <strong>{formatCount(row.total_visits)}</strong>
        </div>
        <div>
          <span>{t("reports:noShow.grossAtRiskMinutes")}</span>
          <strong>{formatMinutes(row.gross_at_risk_minutes)}</strong>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Main component                                                             */
/* -------------------------------------------------------------------------- */

const NoShowLossReportPage = () => {
  const { t } = useTranslation(["common", "reports"]);
  const { authState } = useContext(AuthContext);

  const currentRole = authState?.user?.role || "";
  const currentUserId = authState?.user?.id;

  const allowedEvaluationModes = useMemo(() => {
    if (currentRole === "user") {
      return [EVALUATION_PERIOD_TYPES.MONTH];
    }

    return [
      EVALUATION_PERIOD_TYPES.MONTH,
      EVALUATION_PERIOD_TYPES.YEAR,
      EVALUATION_PERIOD_TYPES.WEEK,
      EVALUATION_PERIOD_TYPES.DAY,
      EVALUATION_PERIOD_TYPES.MANUAL,
    ];
  }, [currentRole, t]);

  const evaluationPeriod = useEvaluationPeriod({
    defaultMode: EVALUATION_PERIOD_TYPES.MONTH,
    initialDate: new Date(),
    allowedModes: allowedEvaluationModes,
  });

  const { from: selectedFrom, to: selectedTo } = evaluationPeriod;

  const [filters, setFilters] = useState(() => ({
    userIds:
      currentRole === "user" && currentUserId != null ? [currentUserId] : [],
    searchText: "",
  }));

  const [users, setUsers] = useState([]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [loadedPeriodMeta, setLoadedPeriodMeta] = useState({
    from: selectedFrom,
    to: selectedTo,
    displayLabel: evaluationPeriod.displayLabel || "",
  });

  useEffect(() => {
    if (currentRole === "user" && currentUserId != null) {
      setFilters((prev) => ({
        ...prev,
        userIds: [currentUserId],
      }));
    }
  }, [currentRole, currentUserId]);

  useEffect(() => {
    const loadUsers = async () => {
      try {
        const userList = await fetchUsers();
        setUsers(Array.isArray(userList) ? userList : []);
      } catch (err) {
        console.error("[NoShowLossReportPage] ❌ Failed to fetch users:", err);
        setError(t("reports:noShow.errors.failedToLoadUsers"));
      }
    };

    if (currentRole !== "user") {
      loadUsers();
    }
  }, [currentRole, t]);

  const currentUserDisplay = useMemo(() => {
    const firstName = authState?.user?.first_name || "";
    const lastName = authState?.user?.last_name || "";
    const username = authState?.user?.username || "";

    const fullName = [firstName, lastName].filter(Boolean).join(" ").trim();

    if (fullName && username) return `${fullName} (${username})`;
    if (fullName) return fullName;
    if (username) return username;

    return t("reports:common.currentUser", { defaultValue: "Current user" });
  }, [
    authState?.user?.first_name,
    authState?.user?.last_name,
    authState?.user?.username,
    t,
  ]);

  const loadReportByRange = useCallback(
    async ({ from, to, userIds, displayLabel }) => {
      if (!from || !to) {
        setError(t("reports:common.invalidEvaluationPeriod"));
        return;
      }

      setLoading(true);
      setError("");

      try {
        const payload = await fetchNoShowLossReport(from, to, userIds);
        setReport(payload || null);

        setLoadedPeriodMeta({
          from,
          to,
          displayLabel: displayLabel || "",
        });
      } catch (err) {
        console.error("[NoShowLossReportPage] ❌ Error fetching report:", err);
        setError(t("reports:noShow.errors.failedToFetch"));
        setReport(null);
      } finally {
        setLoading(false);
      }
    },
    [t]
  );

  const initialRequestRef = useRef({
    from: selectedFrom,
    to: selectedTo,
    userIds:
      currentRole === "user" && currentUserId != null ? [currentUserId] : [],
    displayLabel: evaluationPeriod.displayLabel || "",
  });

  useEffect(() => {
    loadReportByRange(initialRequestRef.current);
  }, [loadReportByRange]);

  const summary = report?.summary || {};
  const methodologyNotes = resolveMethodologyNotes(report?.methodology, t);

  const rows = Array.isArray(report?.rows) ? report.rows : [];
  const byWeekday = Array.isArray(report?.by_weekday) ? report.by_weekday : [];
  const byService = Array.isArray(report?.by_service) ? report.by_service : [];
  const byEmployee = Array.isArray(report?.by_employee) ? report.by_employee : [];

  const visibleRows = useMemo(() => {
    const searchText = String(filters.searchText || "").trim().toLowerCase();

    if (!searchText) return rows;

    return rows.filter((row) => {
      const date = String(row.date || "").toLowerCase();
      const weekday = String(row.weekday_label || "").toLowerCase();
      const service = String(row.service || "").toLowerCase();
      const category = String(row.category || "").toLowerCase();
      const employee = String(row.employee || "").toLowerCase();
      const client = String(row.client || "").toLowerCase();
      const comment = String(row.status_comment || "").toLowerCase();
      const recoveryStatus = String(row.recovery_status || "").toLowerCase();

      return (
        date.includes(searchText) ||
        weekday.includes(searchText) ||
        service.includes(searchText) ||
        category.includes(searchText) ||
        employee.includes(searchText) ||
        client.includes(searchText) ||
        comment.includes(searchText) ||
        recoveryStatus.includes(searchText)
      );
    });
  }, [rows, filters.searchText]);

  const byWeekdayChartData = useMemo(() => {
    return byWeekday.map((row) => ({
      ...row,
      label: row.name,
      gross_at_risk_revenue: toFiniteNumber(row.gross_at_risk_revenue, 0),
      recovered_revenue: toFiniteNumber(row.recovered_revenue, 0),
      net_unrecovered_revenue: toFiniteNumber(row.net_unrecovered_revenue, 0),
      recovery_rate_revenue: toFiniteNumber(row.recovery_rate_revenue, 0),
    }));
  }, [byWeekday]);

  const byServiceTopChartData = useMemo(() => {
    return byService.slice(0, 8).map((row) => ({
      ...row,
      label: row.name,
      gross_at_risk_revenue: toFiniteNumber(row.gross_at_risk_revenue, 0),
      recovered_revenue: toFiniteNumber(row.recovered_revenue, 0),
      net_unrecovered_revenue: toFiniteNumber(row.net_unrecovered_revenue, 0),
    }));
  }, [byService]);

  const hasReportData = Boolean(report);

  const renderAggTable = (title, description, items) => (
    <div className="report-card">
      <div className="card-header">
        <h2>{title}</h2>
        <p>{description}</p>
      </div>

      <div className="table-wrapper">
        <table className="data-table data-table--compact">
          <thead>
            <tr>
              <th>{t("reports:common.name")}</th>
              <th>{t("reports:noShow.noShowVisits")}</th>
              <th>{t("reports:noShow.grossAtRiskRevenue")}</th>
              <th>{t("reports:noShow.recoveredRevenue")}</th>
              <th>{t("reports:noShow.netUnrecoveredRevenue")}</th>
              <th>{t("reports:noShow.recoveryRate")}</th>
              <th>{t("reports:noShow.grossAtRiskMinutes")}</th>
              <th>{t("reports:noShow.netUnrecoveredMinutes")}</th>
            </tr>
          </thead>
          <tbody>
            {items.length > 0 ? (
              items.map((row) => (
                <tr key={row.name}>
                  <td>{row.name}</td>
                  <td>{formatCount(row.total_visits)}</td>
                  <td>{formatMoney(row.gross_at_risk_revenue)}</td>
                  <td>{formatMoney(row.recovered_revenue)}</td>
                  <td>{formatMoney(row.net_unrecovered_revenue)}</td>
                  <td>{formatPercentFromRatio(row.recovery_rate_revenue)}</td>
                  <td>{formatMinutes(row.gross_at_risk_minutes)}</td>
                  <td>{formatMinutes(row.net_unrecovered_minutes)}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={8} className="empty-table-cell">
                  No data available.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div className="no-show-loss-page">
      <div className="no-show-loss-header">
        <div className="header-left">
          <h1>{t("reports:noShow.title")}</h1>
          <p className="header-hint">
            {t("reports:noShow.headerHint")}
          </p>
        </div>
      </div>

      {error && <p className="error-message">{error}</p>}

      <div className="filters-bar">
        <div className="filters-left">
          <EvaluationPeriodPicker period={evaluationPeriod} />

          {currentRole !== "user" ? (
            <div className="filter-item filter-item-users">
              <label className="filter-label">{t("reports:common.employees")}</label>

              <select
                multiple
                className="user-dropdown"
                value={filters.userIds}
                onChange={(e) => {
                  const selectedValues = Array.from(
                    e.target.selectedOptions,
                    (o) => Number(o.value)
                  ).filter(Number.isFinite);

                  setFilters((prev) => ({
                    ...prev,
                    userIds: selectedValues,
                  }));
                }}
              >
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.first_name} {user.last_name} ({user.username})
                  </option>
                ))}
              </select>

              <div className="filter-help">{t("reports:common.multiSelectHelp")}</div>
            </div>
          ) : (
            <div className="filter-item filter-item-users">
              <label className="filter-label">{t("reports:common.employee")}</label>
              <input
                type="text"
                className="user-fixed"
                value={currentUserDisplay}
                readOnly
              />
            </div>
          )}

          <div className="filter-item filter-item-search">
            <label className="filter-label">{t("reports:common.search")}</label>
            <input
              type="text"
              className="search-input"
              value={filters.searchText}
              placeholder={t("reports:noShow.searchPlaceholder")}
              onChange={(e) =>
                setFilters((prev) => ({
                  ...prev,
                  searchText: e.target.value,
                }))
              }
            />
          </div>
        </div>

        <div className="filters-actions">
          <button
            className="apply-button"
            onClick={() =>
              loadReportByRange({
                from: selectedFrom,
                to: selectedTo,
                userIds: filters.userIds,
                displayLabel: evaluationPeriod.displayLabel,
              })
            }
            disabled={loading}
          >
            {loading ? t("reports:common.loading") : t("reports:common.generate")}
          </button>
        </div>
      </div>

      <div className="report-scroll">
        {loading ? (
          <div className="loading-panel">{t("reports:common.loading")}</div>
        ) : hasReportData ? (
          <>
            <div className="summary-grid">
              <div className="summary-card">
                <span className="summary-label">{t("reports:noShow.noShowVisits")}</span>
                <span className="summary-value">
                  {formatCount(summary.no_show_visits_count)}
                </span>
              </div>

              <div className="summary-card">
                <span className="summary-label">{t("reports:noShow.grossAtRiskRevenue")}</span>
                <span className="summary-value">
                  {formatMoney(summary.gross_at_risk_revenue)} PLN
                </span>
              </div>

              <div className="summary-card">
                <span className="summary-label">{t("reports:noShow.recoveredRevenue")}</span>
                <span className="summary-value summary-value--positive">
                  {formatMoney(summary.recovered_revenue)} PLN
                </span>
              </div>

              <div className="summary-card">
                <span className="summary-label">{t("reports:noShow.netUnrecoveredRevenue")}</span>
                <span className="summary-value summary-value--negative">
                  {formatMoney(summary.net_unrecovered_revenue)} PLN
                </span>
              </div>

              <div className="summary-card">
                <span className="summary-label">{t("reports:noShow.revenueRecoveryRate")}</span>
                <span className="summary-value">
                  {formatPercentFromRatio(summary.recovery_rate_revenue)}
                </span>
              </div>

              <div className="summary-card">
                <span className="summary-label">{t("reports:noShow.grossAtRiskMinutes")}</span>
                <span className="summary-value">
                  {formatMinutes(summary.gross_at_risk_minutes)}
                </span>
              </div>

              <div className="summary-card">
                <span className="summary-label">{t("reports:noShow.recoveredMinutes")}</span>
                <span className="summary-value summary-value--positive">
                  {formatMinutes(summary.recovered_minutes)}
                </span>
              </div>

              <div className="summary-card">
                <span className="summary-label">{t("reports:noShow.netUnrecoveredMinutes")}</span>
                <span className="summary-value summary-value--negative">
                  {formatMinutes(summary.net_unrecovered_minutes)}
                </span>
              </div>

              <div className="summary-card">
                <span className="summary-label">{t("reports:noShow.sameWeekRecoveryRate")}</span>
                <span className="summary-value">
                  {formatPercentFromRatio(summary.same_week_recovery_rate)}
                </span>
              </div>

              <div className="summary-card">
                <span className="summary-label">{t("reports:noShow.sameMonthRecoveryRate")}</span>
                <span className="summary-value">
                  {formatPercentFromRatio(summary.same_month_recovery_rate)}
                </span>
              </div>
            </div>

            <div className="highlight-grid">
              <div className="highlight-card">
                <div className="highlight-title">{t("reports:noShow.topNetImpactWeekday")}</div>
                {summary?.top_net_loss_weekday ? (
                  <>
                    <div className="highlight-name">
                      {summary.top_net_loss_weekday.name}
                    </div>
                    <div className="highlight-metric">
                      {formatMoney(summary.top_net_loss_weekday.net_unrecovered_revenue)} PLN
                    </div>
                  </>
                ) : (
                  <div className="highlight-empty">{t("reports:common.noDataAvailable")}</div>
                )}
              </div>

              <div className="highlight-card">
                <div className="highlight-title">{t("reports:noShow.topNetImpactService")}</div>
                {summary?.top_net_loss_service ? (
                  <>
                    <div className="highlight-name">
                      {summary.top_net_loss_service.name}
                    </div>
                    <div className="highlight-metric">
                      {formatMoney(summary.top_net_loss_service.net_unrecovered_revenue)} PLN
                    </div>
                  </>
                ) : (
                  <div className="highlight-empty">{t("reports:common.noDataAvailable")}</div>
                )}
              </div>

              <div className="highlight-card">
                <div className="highlight-title">{t("reports:noShow.topNetImpactEmployee")}</div>
                {summary?.top_net_loss_employee ? (
                  <>
                    <div className="highlight-name">
                      {summary.top_net_loss_employee.name}
                    </div>
                    <div className="highlight-metric">
                      {formatMoney(summary.top_net_loss_employee.net_unrecovered_revenue)} PLN
                    </div>
                  </>
                ) : (
                  <div className="highlight-empty">{t("reports:common.noDataAvailable")}</div>
                )}
              </div>
            </div>

            <div className="methodology-card">
              <div className="methodology-title">{t("reports:noShow.methodology")}</div>
              <ul className="methodology-list">
                {methodologyNotes.map((note, idx) => (
                  <li key={idx}>{note}</li>
                ))}
              </ul>

              {(loadedPeriodMeta?.from || loadedPeriodMeta?.to) && (
                <div className="loaded-period-note">
                  {t("reports:common.loadedPeriod")}: <strong>{loadedPeriodMeta.from}</strong> →{" "}
                  <strong>{loadedPeriodMeta.to}</strong>
                  {loadedPeriodMeta.displayLabel ? (
                    <> ({loadedPeriodMeta.displayLabel})</>
                  ) : null}
                </div>
              )}

              <div className="interpretation-note">
                {t("reports:noShow.interpretationNote")}
              </div>
            </div>

            <div className="charts-grid">
              <div className="report-card chart-card chart-card--wide">
                <div className="card-header">
                  <h2>{t("reports:noShow.impactByWeekday")}</h2>
                  <p>
                    {t("reports:noShow.impactByWeekdayDescription")}
                  </p>
                </div>

                <div className="chart-container chart-container--large">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart
                      data={byWeekdayChartData}
                      margin={{ top: 10, right: 24, left: 0, bottom: 8 }}
                    >
                      <CartesianGrid stroke={CHART_COLORS.grid} strokeDasharray="3 3" />
                      <XAxis
                        dataKey="label"
                        tick={{ fill: CHART_COLORS.axis, fontSize: 12 }}
                      />
                      <YAxis
                        yAxisId="left"
                        tick={{ fill: CHART_COLORS.axis, fontSize: 12 }}
                      />
                      <YAxis
                        yAxisId="right"
                        orientation="right"
                        domain={[0, 1]}
                        tick={{ fill: CHART_COLORS.axis, fontSize: 12 }}
                        tickFormatter={(value) => `${Math.round(value * 100)}%`}
                      />
                      <Tooltip content={<ImpactBreakdownTooltip t={t} />} />
                      <Legend />

                      <Bar yAxisId="left" dataKey="gross_at_risk_revenue" name={t("reports:noShow.grossAtRisk")} fill={CHART_COLORS.gross} radius={[6, 6, 0, 0]} />
                      <Bar yAxisId="left" dataKey="recovered_revenue" name={t("reports:noShow.recovered")} fill={CHART_COLORS.recovered} radius={[6, 6, 0, 0]} />
                      <Bar yAxisId="left" dataKey="net_unrecovered_revenue" name={t("reports:noShow.netUnrecovered")} fill={CHART_COLORS.net} radius={[6, 6, 0, 0]} />
                      <Line
                        yAxisId="right"
                        type="monotone"
                        dataKey="recovery_rate_revenue"
                        name={t("reports:noShow.recoveryRate")}
                        stroke={CHART_COLORS.rate}
                        strokeWidth={2.5}
                        dot={{ r: 3, fill: CHART_COLORS.rate }}
                        activeDot={{ r: 5, fill: CHART_COLORS.rate }}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="report-card chart-card">
                <div className="card-header">
                  <h2>{t("reports:noShow.topServicesByNetUnrecoveredRevenue")}</h2>
                  <p>
                    {t("reports:noShow.topServicesByNetDescription")}
                  </p>
                </div>

                <div className="chart-container">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={byServiceTopChartData}
                      layout="vertical"
                      margin={{ top: 10, right: 24, left: 24, bottom: 8 }}
                    >
                      <CartesianGrid stroke={CHART_COLORS.grid} strokeDasharray="3 3" />
                      <XAxis
                        type="number"
                        tick={{ fill: CHART_COLORS.axis, fontSize: 12 }}
                      />
                      <YAxis
                        type="category"
                        dataKey="label"
                        width={150}
                        tick={{ fill: CHART_COLORS.axis, fontSize: 12 }}
                      />
                      <Tooltip content={<ImpactBreakdownTooltip t={t} />} />
                      <Legend />
                      <Bar dataKey="net_unrecovered_revenue" name={t("reports:noShow.netUnrecovered")} fill={CHART_COLORS.net} radius={[0, 6, 6, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="report-card chart-card">
                <div className="card-header">
                  <h2>{t("reports:noShow.topServicesByRecoveredRevenue")}</h2>
                  <p>
                    {t("reports:noShow.topServicesByRecoveredDescription")}
                  </p>
                </div>

                <div className="chart-container">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={byServiceTopChartData}
                      layout="vertical"
                      margin={{ top: 10, right: 24, left: 24, bottom: 8 }}
                    >
                      <CartesianGrid stroke={CHART_COLORS.grid} strokeDasharray="3 3" />
                      <XAxis
                        type="number"
                        tick={{ fill: CHART_COLORS.axis, fontSize: 12 }}
                      />
                      <YAxis
                        type="category"
                        dataKey="label"
                        width={150}
                        tick={{ fill: CHART_COLORS.axis, fontSize: 12 }}
                      />
                      <Tooltip content={<ImpactBreakdownTooltip t={t} />} />
                      <Legend />
                      <Bar dataKey="recovered_revenue" name={t("reports:noShow.recovered")} fill={CHART_COLORS.recovered} radius={[0, 6, 6, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            {renderAggTable(
              t("reports:noShow.impactByWeekday"),
              t("reports:noShow.impactByWeekdayTableDescription"),
              byWeekday
            )}

            {renderAggTable(
              t("reports:noShow.impactByService"),
              t("reports:noShow.impactByServiceDescription"),
              byService
            )}

            {renderAggTable(
              t("reports:noShow.impactByEmployee"),
              t("reports:noShow.impactByEmployeeDescription"),
              byEmployee
            )}

            <div className="report-card">
              <div className="card-header">
                <h2>{t("reports:noShow.detailedRows")}</h2>
                <p>
                  {t("reports:noShow.detailedRowsDescription")}
                </p>
              </div>

              <div className="table-wrapper">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>{t("reports:common.date")}</th>
                      <th>{t("reports:noShow.weekday")}</th>
                      <th>{t("reports:common.service")}</th>
                      <th>{t("reports:common.category")}</th>
                      <th>{t("reports:common.employee")}</th>
                      <th>{t("reports:common.client")}</th>
                      <th>{t("reports:noShow.grossAtRisk")}</th>
                      <th>{t("reports:noShow.recovered")}</th>
                      <th>{t("reports:noShow.netUnrecovered")}</th>
                      <th>{t("reports:common.status")}</th>
                      <th>{t("reports:noShow.lagDays")}</th>
                      <th>{t("reports:noShow.confidence")}</th>
                      <th>{t("reports:noShow.rebookedDate")}</th>
                      <th>{t("reports:noShow.statusComment")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleRows.length > 0 ? (
                      visibleRows.map((row) => (
                        <tr key={`${row.visit_id}-${row.date}`}>
                          <td>{row.date}</td>
                          <td>{row.weekday_label}</td>
                          <td>{row.service}</td>
                          <td>{row.category}</td>
                          <td>{row.employee}</td>
                          <td>{row.client || "—"}</td>
                          <td>{formatMoney(row.gross_at_risk_revenue)}</td>
                          <td>{formatMoney(row.recovered_revenue_estimate)}</td>
                          <td>{formatMoney(row.net_unrecovered_revenue)}</td>
                          <td>{row.recovery_status}</td>
                          <td>
                            {row.recovery_lag_days == null
                              ? "—"
                              : formatCount(row.recovery_lag_days)}
                          </td>
                          <td>{row.heuristic_match_confidence || "—"}</td>
                          <td>{row.rebooked_date || "—"}</td>
                          <td className="wrap-cell">{row.status_comment || "—"}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={14} className="empty-table-cell">
                          {t("reports:noShow.noRowsMatchFilters")}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        ) : (
          <div className="empty-panel">{t("reports:common.noDataAvailable")}</div>
        )}
      </div>
    </div>
  );
};

export default NoShowLossReportPage;